import React, { useEffect, useState } from 'react';
import { Box, Text } from 'grommet';
import CustomerTag from './CustomerTag';
import GroupTag from './GroupTag';
import { formatName } from '../utils/names';
import { handleErrors } from '../utils/errors';
import { useDebounce } from '../utils/debounce';
import useSWR from 'swr';
import {TextInput, List} from "grommet";
import {
    faMagnifyingGlass as IconSearch,
    faUserGroup as IconGroup
} from '@fortawesome/pro-light-svg-icons';
import FontAwesomeIcon from "./FontAwesomeIcon";
import Badge from './Badge';

const CustomerTagInput = ({
    onChange,
    value,
    isSingleSelect, // only allow selecting one customer?
    includeGroups = false // include customer groups in search results?
}) => {
    const [selectedTags, setSelectedTags] = useState(value || []);
    const [keyword, setKeyword] = useState('');
    const debouncedKeyword = useDebounce(keyword, 400);
    
    useEffect(() => {
        // set preset, if given
        if (value.length === 0 && selectedTags.length === 0 || !isSingleSelect) return;
        setSelectedTags(value);
    }, [value]);

    useEffect(() => {
        onChange && onChange({value: selectedTags}); // when user chooses a customer from suggestion
    }, [selectedTags]);

    const fetcher = (args) => {
        return fetch(`${args[0]}?${args[1]}`)
            .then(handleErrors);
    }

    // Fetch customers
    const { data: suggestedCustomers } = useSWR(
        keyword && (!isSingleSelect || selectedTags.length === 0) ?
            [`/api/customers`, new URLSearchParams({
                ...({
                    limit: 5,
                    offset: 0,
                    keyword,
                    excluded_ids: selectedTags
                }),
                ...(debouncedKeyword && {keyword: debouncedKeyword.trim()})
            }).toString()] :
            null,
        fetcher
    );

    // Fetch groups (only if includeGroups is true)
    const { data: suggestedGroups } = useSWR(
        includeGroups && keyword && (!isSingleSelect || selectedTags.length === 0) ?
            [`/api/customers/groups`, new URLSearchParams({
                limit: 5,
                offset: 0,
                keyword: debouncedKeyword?.trim() || keyword
            }).toString()] :
            null,
        fetcher
    );

    const renderSuggestions = () => {
        if (!keyword) return [];

        const suggestions = [];

        // Add customer suggestions
        if (suggestedCustomers?.customers) {
            suggestedCustomers.customers.forEach((customer) => {
                const customerName = customer.company_name || formatName(customer, true);
                const contactName = customer.company_name ? formatName(customer, true) : null;

                suggestions.push({
                    label: (
                        <Box pad={{ vertical: 'xsmall', horizontal: 'small' }} style={{ overflow: 'hidden', minWidth: 0 }}>
                            <Text truncate>{customerName}</Text>
                            {contactName && <Text size="xsmall" color="text-weak" truncate>{contactName}</Text>}
                        </Box>
                    ),
                    value: includeGroups ? { type: 'customer', id: customer.id } : customer.id,
                });
            });
        }

        // Add group suggestions
        if (includeGroups && suggestedGroups?.customer_groups) {
            suggestedGroups.customer_groups.forEach((group) => {
                const groupName = group.company_name || group.name;

                suggestions.push({
                    label: (
                        <Box direction="row" align="center" justify="between" pad={{ vertical: 'xsmall', horizontal: 'small' }} style={{ overflow: 'hidden', minWidth: 0 }}>
                            <Text truncate>{groupName}</Text>
                            <Badge icon={IconGroup} size="small">Group</Badge>
                        </Box>
                    ),
                    value: { type: 'group', id: group.id },
                });
            });
        }

        return suggestions;
    };

    const onRemoveTag = (tag) => {
        let newTags;
        if (includeGroups && typeof tag === 'object') {
            newTags = selectedTags.filter(t => !(t.type === tag.type && t.id === tag.id));
        } else {
            const removeIndex = selectedTags.indexOf(tag);
            newTags = [...selectedTags];
            if (removeIndex >= 0) {
                newTags.splice(removeIndex, 1);
            }
        }
        setSelectedTags(newTags);
    };

    const onAddTag = (tag) => {
        if (isSingleSelect && selectedTags.length > 0) return;
        const newTags = [tag, ...selectedTags];
        setSelectedTags(newTags);
        setKeyword('');
    }

    return (
        <Box pad='xsmall'>
            {(!isSingleSelect || selectedTags.length === 0) && (
                <Box>
                    <TextInput
                        type="search"
                        icon={<FontAwesomeIcon SelectedIcon={IconSearch} color="text-xweak" />}
                        placeholder="Add customer"
                        value={keyword}
                        onChange={({ target: { value } }) => setKeyword(value)}
                        suggestions={renderSuggestions()}
                        onSuggestionSelect={({ suggestion }) => onAddTag(suggestion.value)}
                        dropProps={{ stretch: true, overflow: "hidden" }}
                    />
                </Box>
            )}
            <Box height="medium" pad={{vertical: 'xxsmall'}} overflow="auto" border={{side: 'top', color: 'border-weak'}} margin={{top: 'xsmall'}}>
                {selectedTags.length > 0 ? (
                    <List
                        data={selectedTags}
                        pad="xxsmall">
                        {(item) => {
                            const isObject = typeof item === 'object';
                            const itemId = isObject ? item.id : item;
                            const itemType = isObject ? item.type : 'customer';
                            const key = isObject ? `${item.type}-${item.id}` : item;

                            return (
                                <Box
                                    key={key}
                                    direction="row"
                                    responsive={false}
                                    pad={{vertical: 'xxsmall'}}
                                >
                                    {itemType === 'group' ? (
                                        <GroupTag
                                            inlineBlock={false}
                                            onRemove={() => onRemoveTag(item)}
                                            id={itemId}
                                        />
                                    ) : (
                                        <CustomerTag
                                            inlineBlock={false}
                                            onRemove={() => onRemoveTag(item)}
                                            id={itemId}
                                        />
                                    )}
                                </Box>
                            );
                        }}
                    </List>
                ):(
                    <Box
                        height="100%"
                        align="center"
                        justify="center"
                        gap="small"
                    >
                        <Text textAlign="center">No customers selected</Text>
                        <Text size="small" margin="none" textAlign="center" color="text-weak">Use the search box above to<br/> add customers to this group</Text>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default CustomerTagInput;
